import { useState } from 'react'
import { Alert, Modal, Typography } from 'antd'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { deleteCenter, deleteVisp } from './networkApi'

export type NetworkDeleteTarget = { kind: 'center' | 'visp'; id: string; name: string }

export function NetworkDeleteModal({
  target,
  onClose,
}: {
  target: NetworkDeleteTarget | null
  onClose: () => void
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const confirm = async () => {
    if (!target) return
    setDeleting(true)
    setError(null)
    try {
      await (target.kind === 'center' ? deleteCenter(target.id) : deleteVisp(target.id))
      void queryClient.invalidateQueries({ queryKey: queryKeys.network.all })
      onClose()
    } catch (err) {
      setError(translateError(err, t))
    } finally {
      setDeleting(false)
    }
  }

  return (
    <Modal
      open={target !== null}
      title={target?.kind === 'center' ? t('network.centers.deleteTitle') : t('network.visps.deleteTitle')}
      onCancel={onClose}
      onOk={() => void confirm()}
      okText={t('network.delete')}
      okButtonProps={{ danger: true }}
      cancelText={t('common.cancel')}
      confirmLoading={deleting}
      destroyOnHidden
      afterOpenChange={(visible) => {
        if (visible) setError(null)
      }}
    >
      {error ? <Alert type="error" showIcon style={{ marginBottom: 16 }} message={error} /> : null}
      <Typography.Paragraph>{t('network.deleteConfirm', { name: target?.name ?? '' })}</Typography.Paragraph>
    </Modal>
  )
}
